import React, { useMemo } from "react";
import { Loader2, User, MessageSquare, Clock } from "lucide-react";
import { useSponsorConversationLogsQuery } from "@/lib/api/requests/sponsors.requests";
import type { SponsorConversationLogResponseDto } from "@/lib/api/interfaces/sponsor-conversation-logs.interface";

interface SponsorConversationLogSectionProps {
  sponsorId: string;
}

export const SponsorConversationLogSection: React.FC<SponsorConversationLogSectionProps> = ({ sponsorId }) => {
  const logsQuery = useSponsorConversationLogsQuery(sponsorId, {
    enabled: !!sponsorId,
  });

  const logs: SponsorConversationLogResponseDto[] = useMemo(
    () => {
      const allLogs = Array.isArray(logsQuery.data) ? logsQuery.data : [];
      return allLogs.slice(0, 3);
    },
    [logsQuery.data]
  );

  return (
    <div className="h-full">
      {logsQuery.isLoading ? (
        <div className="flex h-32 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <p className="text-xs text-gray-500 font-medium">Historie wird geladen...</p>
          </div>
        </div>
      ) : logsQuery.isError ? (
        <div className="rounded-2xl bg-rose-50 p-4 border border-rose-100 text-sm text-rose-700 flex items-center gap-3">
          <div className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
          Gesprächsprotokoll konnte nicht geladen werden.
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 px-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 text-center">
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <MessageSquare className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-900">Keine Einträge vorhanden</p>
          <p className="text-xs text-gray-500 mt-1 max-w-[200px]">
            Es wurden noch keine Gespräche oder Notizen protokolliert.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log, idx) => {
            const dateSource = log.conversationAt || log.createdAt;
            const date = dateSource ? new Date(dateSource) : null;

            return (
              <div
                key={log._id ?? `${dateSource ?? "no-date"}-${idx}`}
                className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white p-4 transition-colors hover:border-gray-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50">
                      <User className="h-3.5 w-3.5 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {log.authorName || "Unbekannter Benutzer"}
                      </p>
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-400">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span className="truncate">
                          {date
                            ? date.toLocaleString("de-DE", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })
                            : "Ohne Datum"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pl-10">
                  {log.content && log.content.trim().length > 0 ? (
                    <p className="line-clamp-3 whitespace-pre-line text-xs leading-relaxed text-gray-600">
                      {log.content}
                    </p>
                  ) : (
                    <p className="text-xs italic text-gray-400">
                      (Kein Inhalt)
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
